/**
 * Dragging assets onto folders.
 *
 * The payload rides on the drag event's own `dataTransfer` rather than in React
 * state, because a drag crosses component trees — a tile in the grid, a node in
 * the rail — and the browser already carries data along that path. Shared state
 * would also survive a cancelled drag and leave a stale "currently dragging"
 * flag behind.
 *
 * A custom MIME type keeps our payload distinguishable from a file drag: the
 * grid is already a drop zone for uploads from the desktop, and dropping a
 * folder full of photos must not be mistaken for an asset move.
 */
import type { Id } from '@shared/types';

/** Our own drag type. Files dragged from the OS never carry this. */
export const ASSET_MIME = 'application/x-dam-asset';

export interface AssetDragPayload {
  id: Id;
  fileName: string;
  /** Where it currently lives, so a drop onto the same folder can be ignored. */
  folderId: Id | null;
  /**
   * The whole selection, when the dragged item is part of one — dragging a
   * selected tile moves everything selected, which is what people expect from
   * a file manager. Absent for a plain single-item drag.
   */
  ids?: Id[];
}

/** Mark a drag as carrying one of our assets. */
export function setAssetDrag(e: React.DragEvent, payload: AssetDragPayload): void {
  e.dataTransfer.setData(ASSET_MIME, JSON.stringify(payload));
  // `move` gives the cursor the right affordance and stops browsers offering
  // to open the item as a link.
  e.dataTransfer.effectAllowed = 'move';
}

/**
 * Read our payload from a drop, or null if this drag is something else
 * (a file from the desktop, text, another app).
 */
export function readAssetDrag(e: React.DragEvent): AssetDragPayload | null {
  const raw = e.dataTransfer.getData(ASSET_MIME);
  if (!raw) return null;
  try {
    const p = JSON.parse(raw) as AssetDragPayload;
    return typeof p?.id === 'number' ? p : null;
  } catch {
    return null;
  }
}

/**
 * Whether a drag *looks* like one of ours, during dragover.
 *
 * `getData` is deliberately blocked while a drag is in flight — only the types
 * are readable — so hover highlighting has to go on the type list alone.
 */
export function isAssetDrag(e: React.DragEvent): boolean {
  return e.dataTransfer.types.includes(ASSET_MIME);
}
